"use client";

import { useMemo } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils/cn";

const LABELS: Record<string, string> = {
  overview: "Overview", dashboard: "Fleet", devices: "Data loggers", "master-view": "Master View",
  live: "Live", explorer: "Explorer", analytics: "Analytics", dashboards: "Dashboards",
  automations: "Alerts", reports: "Reports", alerts: "Notifications", notifications: "Notifications",
  users: "Users", "platform-users": "Platform Users", "platform-roles": "Roles", tenants: "Workspaces",
  permissions: "Permissions", organizations: "Organizations", billing: "Billing", support: "Support",
  security: "Security", "system-health": "Health", new: "New",
};

function toDisplayLabel(part: string): string {
  return part.replace(/[-_]/g, " ").split(" ").filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

export type WorkspacePathCrumb = { href: string; label: string };

export function buildWorkspaceCrumbsFromPath(pathname: string): WorkspacePathCrumb[] {
  const parts = (pathname.split("?", 1)[0] ?? pathname).split("/").filter(Boolean);
  const crumbs: WorkspacePathCrumb[] = [];
  let href = "";
  for (const part of parts) {
    href += `/${part}`;
    crumbs.push({ href, label: LABELS[part] ?? toDisplayLabel(part) });
  }
  return crumbs;
}

export type WorkspaceBreadcrumbProps = { pathname: string; className?: string };

export function WorkspaceBreadcrumb({ pathname, className }: WorkspaceBreadcrumbProps) {
  const items = useMemo(() => buildWorkspaceCrumbsFromPath(pathname), [pathname]);
  if (items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0 text-caption", className)}>
      <ol className="flex max-w-full flex-wrap items-center gap-x-ds-2 gap-y-ds-1">
        <li><Link to="/overview" className="rounded-control px-ds-1 py-ds-0.5 text-muted transition-colors hover:bg-surface-hover hover:text-foreground">Workspace</Link></li>
        {items.map((crumb, index) => (
          <li key={crumb.href} className="flex min-w-0 items-center gap-ds-2">
            <span className="text-border" aria-hidden>/</span>
            {index === items.length - 1
              ? <span className="truncate font-medium text-foreground" aria-current="page">{crumb.label}</span>
              : <Link to={crumb.href} className="truncate rounded-control px-ds-1 py-ds-0.5 text-muted transition-colors hover:bg-surface-hover hover:text-foreground">{crumb.label}</Link>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function WorkspaceHeaderTitle({ pathname, className }: WorkspaceBreadcrumbProps) {
  const items = useMemo(() => buildWorkspaceCrumbsFromPath(pathname), [pathname]);
  const last = items[items.length - 1];
  if (!last) return null;
  return <p className={cn("truncate font-medium text-foreground", className)} title={last.label}>{last.label}</p>;
}
